import { useCallback, useEffect, useRef, useState } from "react";
import useIsMobile from "./hooks/useIsMobile";
import { T, base, injectGlobalStyles } from "./lib/theme";
import { useTheme } from "./lib/ThemeContext";
import { ThemeProvider } from "./lib/ThemeContext";
import { apiFetch }  from "./lib/api";
import { cleanPhone } from "./lib/utils";
import useSocket      from "./hooks/useSocket";
import Sidebar         from "./components/Sidebar";
import ConnectionModal from "./components/ConnectionModal";
import { Btn }  from "./components";
import Icon            from "./components/Icon";
import UpgradeModal    from "./components/UpgradeModal";
import {
  AuthScreen, OverviewPage, ChatsPage,
  BroadcastPage, SchedulePage, KnowledgePage, SafetyPage, InsightsPage, PlanPage, ContactsPage, PipelinePage, ProfilePage, ShopifyPage, WooCommercePage, EcomPipelinePage,
  InvitePage, OutboundPage, TodoPage, EmailPage, TeamPage,
} from "./pages";
import MessengerPage     from "./pages/MessengerPage";
import InstagramPage     from "./pages/InstagramPage";
import NotificationBell  from "./components/NotificationBell";

// Pages removed from the product — any stale saved page here bounces to Overview.
// (The e-commerce / Meta pages are NOT here: they're admin-gated and off by default.)
const REMOVED_PAGES = ["numbers"];

// Decode JWT payload (no verification — just reading claims for UI)
function decodeJwt(token) {
  try {
    const payload = token.split(".")[1];
    return JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
  } catch { return {}; }
}

const PAGE_META = {
  overview:  { title: "Overview",          sub: "Dashboard & live stats"            },
  chats:     { title: "Chats",             sub: "All customer conversations"         },
  broadcast: { title: "Broadcast",         sub: "Send to multiple contacts at once"  },
  schedule:  { title: "Schedule",          sub: "Auto-reply hours & offline message"  },
  knowledge: { title: "Knowledge Base",    sub: "AI answers from your business info"  },
  safety:    { title: "Safety & Anti-Ban", sub: "Rate limits & human-like behaviour"  },
  insights:  { title: "AI Insights",       sub: "Conversation analysis & improvement tips" },
  plan:      { title: "My Plan & Usage",   sub: "Limits, credits & upgrade options"        },
  contacts:  { title: "Contacts",           sub: "Lead profiles, intentions & history"      },
  pipeline:      { title: "Lead Gen Pipeline",    sub: "AI-powered CRM & lead tracking"            },
  ecom_pipeline: { title: "E-com Pipeline",      sub: "Order confirmation & follow-up automation"  },
  profile:       { title: "My Profile",          sub: "Account settings & security"               },
  messenger:     { title: "Facebook Messenger",  sub: "AI bot for your Facebook Page"              },
  instagram:     { title: "Instagram DMs",       sub: "AI bot for your Instagram Business account"  },
  team:          { title: "Team Members",        sub: "Invite agents & admins to your workspace"    },
  numbers:       { title: "WhatsApp Numbers",    sub: "Manage multiple WhatsApp lines"               },
  outbound:      { title: "Outbound Leads",      sub: "Auto-message leads from forms & spreadsheets" },
  todo:          { title: "To Do",               sub: "Tasks & reminders"                             },
  email:         { title: "Email Assistant",     sub: "AI drafts and replies for your connected inbox" },
};

/* ── Reminder Notification Card ─────────────────────────── */
function ReminderCard({ card, onDismiss }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
  }, []);

  const handleDismiss = () => {
    setVisible(false);
    setTimeout(onDismiss, 280);
  };

  return (
    <div style={{
      pointerEvents: "auto",
      width: 340,
      background: T.bg1,
      border: `1px solid ${T.amber}55`,
      borderRadius: 16,
      overflow: "hidden",
      boxShadow: T.shadowLg,
      transform: visible ? "translateY(0) scale(1)" : "translateY(24px) scale(0.95)",
      opacity: visible ? 1 : 0,
      transition: "transform 0.28s cubic-bezier(0.34,1.56,0.64,1), opacity 0.28s ease",
    }}>
      {/* Amber top stripe */}
      <div style={{ height: 3, background: `linear-gradient(90deg, ${T.amber}, #fbbf24)` }} />

      {/* Body */}
      <div style={{ padding: "16px 18px 18px" }}>
        {/* Header row */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: card.note ? 12 : 16 }}>
          <div style={{
            width: 40, height: 40, borderRadius: 12, flexShrink: 0,
            background: T.amberDim, border: `1px solid ${T.amber}44`,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.amber} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
              <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
            </svg>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: T.amber, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 2 }}>
              Task Reminder
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, color: T.text1, lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {card.title}
            </div>
          </div>
          <button
            onClick={handleDismiss}
            style={{
              width: 28, height: 28, borderRadius: 8, border: `1px solid ${T.border1}`,
              cursor: "pointer", flexShrink: 0, background: T.bg2,
              display: "flex", alignItems: "center", justifyContent: "center",
              transition: "background 0.15s",
            }}
            onMouseEnter={e => e.currentTarget.style.background = T.bg3}
            onMouseLeave={e => e.currentTarget.style.background = T.bg2}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {card.note && (
          <div style={{
            padding: "10px 12px", background: T.bg2, border: `1px solid ${T.border1}`,
            borderRadius: 10, fontSize: 13, color: T.text2, lineHeight: 1.5, marginBottom: 14,
          }}>
            {card.note}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button
            onClick={handleDismiss}
            style={{
              padding: "8px 20px", borderRadius: 9, border: `1px solid ${T.amber}44`,
              background: T.amberDim, color: T.amber,
              fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
              transition: "all 0.15s",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = `${T.amber}22`; }}
            onMouseLeave={e => { e.currentTarget.style.background = T.amberDim; }}
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
}

function AppInner() {
  useTheme(); // subscribe to theme changes so all inline T.* styles re-render
  const [token,         setToken]         = useState(() => localStorage.getItem("token"));
  const userClaims = token ? decodeJwt(token) : {};
  const userRole   = userClaims.role  || "owner";   // "owner" | "admin" | "agent"
  const userType   = userClaims.type  || "owner";   // "owner" | "member"
  const isOwner    = userType === "owner";
  const memberAccess = Array.isArray(userClaims.accessPages) ? userClaims.accessPages : null;
  const [page,          setPage]          = useState(() => localStorage.getItem("zpliq_page") || "overview");
  const [chats,         setChats]         = useState([]);
  const [broadcasts,    setBroadcasts]    = useState([]);
  const [showConnModal, setShowConnModal] = useState(false);
  const [toasts,        setToasts]        = useState([]);
  const [reminderCards, setReminderCards] = useState([]);
  const [apiBusyCount,  setApiBusyCount]  = useState(0);
  const [maintenance,   setMaintenance]   = useState(false);
  const [announcement,  setAnnouncement]  = useState("");
  const [userPlan,      setUserPlan]      = useState("free");
  const [planInfo,      setPlanInfo]      = useState(null);
  const [userCredits,   setUserCredits]   = useState(null);
  const [hasEcomStore,  setHasEcomStore]  = useState(false); // true if shopify OR woocommerce is connected
  const [features,      setFeatures]      = useState(null);  // null = still loading
  const [overviewStats, setOverviewStats] = useState(null);  // mode-aware dashboard stats
  const [upgradeModal,  setUpgradeModal]  = useState({ open: false, reason: null });
  const [sidebarOpen,   setSidebarOpen]   = useState(false);
  const limitModalShown = useRef(false); // prevent re-firing on every render
  const isMobile = useIsMobile();
  const isFreeLikePlan = ["free", "trial"].includes(planInfo?.plan);

  const showUpgradeModal = useCallback((reason = "default") => {
    setUpgradeModal({ open: true, reason });
  }, []);

  useEffect(() => { injectGlobalStyles(); }, []);

  useEffect(() => {
    const onBusy = (event) => setApiBusyCount(count => Math.max(0, count + (event.detail || 0)));
    window.addEventListener("zpliq:api-busy", onBusy);
    return () => window.removeEventListener("zpliq:api-busy", onBusy);
  }, []);

  const canAccessPage = useCallback((id) => {
    if (isFreeLikePlan && ["team", "numbers", "outbound"].includes(id)) return false;
    if (isOwner) return true;
    if (userRole === "agent" && id === "knowledge") return false;
    return Array.isArray(memberAccess) ? memberAccess.includes(id) : ["overview", "chats", "contacts", "pipeline", "schedule", "knowledge", "profile"].includes(id);
  }, [isOwner, userRole, memberAccess, isFreeLikePlan]);

  useEffect(() => {
    if (!token || canAccessPage(page)) return;
    const fallback = canAccessPage("overview") ? "overview" : canAccessPage("chats") ? "chats" : "profile";
    localStorage.setItem("zpliq_page", fallback);
    setPage(fallback);
  }, [token, page, canAccessPage]);

  // Bounce any removed/retired page to Overview so stale saved state can't land there.
  useEffect(() => {
    if (REMOVED_PAGES.includes(page)) {
      localStorage.setItem("zpliq_page", "overview");
      setPage("overview");
    }
  }, [page]);

  // ── Handle email verification redirect ──────────────────────────
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const loginToken  = params.get("login_token");
    const verifyError = params.get("verify_error");
    if (loginToken) {
      localStorage.setItem("token", loginToken);
      setToken(loginToken);
      window.history.replaceState({}, "", window.location.pathname);
      return;
    }
    if (verifyError) {
      setTimeout(() => window.history.replaceState({}, "", window.location.pathname), 5000);
    }
  }, []);

  // ── Handle Meta OAuth callback redirect ─────────────────────────
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const metaOauth = params.get("meta_oauth");
    const emailOauth = params.get("email_oauth");
    if (!metaOauth && !emailOauth) return;
    const platform = params.get("platform") || "messenger";
    const targetPage = emailOauth ? "email" : platform === "instagram" ? "instagram" : "messenger";
    localStorage.setItem("zpliq_page", targetPage);
    setPage(targetPage);
  }, []);

  // ── Toast system ────────────────────────────────────────────────
  const showToast = useCallback((msg, type = "success", duration = 3800) => {
    const id = Date.now();
    setToasts(p => [...p, { id, msg, type }]);
    setTimeout(() => setToasts(p => p.filter(t => t.id !== id)), duration);
  }, []);

  // ── Incoming message handler ─────────────────────────────────────
  const handleNewMessage = useCallback((data) => {
    setChats(prev => {
      const exists = prev.find(c => c.contact === data.contact);
      const updated = {
        contact:       data.contact,
        contactName:   data.contactName || cleanPhone(data.contact),
        lastMessage:   data.reply?.content || data.message?.content || "",
        lastMessageAt: new Date().toISOString(),
        isHumanMode:   data.isHumanMode ?? false,
      };
      if (exists) {
        return prev.map(c => c.contact === data.contact
          ? { ...c, ...updated, unreadCount: page === "chats" ? 0 : data.updateOnly ? (c.unreadCount || 0) : (c.unreadCount || 0) + 1 }
          : c
        );
      }
      return [{ ...updated, unreadCount: page === "chats" || data.updateOnly ? 0 : 1 }, ...prev];
    });
  }, [page]);

  const handleMaintenance = useCallback((data) => {
    setMaintenance(data.maintenanceMode || false);
    setAnnouncement(data.announcement || "");
  }, []);

  const { socket, status, setStatus, qr, sessionInfo } = useSocket(token, handleNewMessage, handleMaintenance, isOwner);

  // ── Request browser notification permission once on login ────────
  useEffect(() => {
    if (!token) return;
    if (typeof Notification !== "undefined" && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, [token]);

  // ── Todo reminder notifications ─────────────────────────────────
  useEffect(() => {
    if (!socket) return;
    const handler = (data) => {
      const id = Date.now();
      setReminderCards(p => [...p, { id, title: data.title, note: data.note || "" }]);
      if (typeof Notification !== "undefined" && Notification.permission === "granted") {
        new Notification("Task Reminder", {
          body: data.note ? `${data.title}\n${data.note}` : data.title,
          icon: "/logo.svg",
        });
      }
    };
    socket.on("todo_reminder", handler);
    return () => socket.off("todo_reminder", handler);
  }, [socket]);

  // ── Load initial data ───────────────────────────────────────────
  useEffect(() => {
    if (!token) return;
    apiFetch("/api/chats",      {}, token).then(d => setChats(d.chats        || [])).catch(() => {});
    apiFetch("/api/broadcasts", {}, token).then(d => setBroadcasts(d.broadcasts || [])).catch(() => {});
    // Fetch maintenance state from main server on load
    apiFetch("/api/maintenance", {}, token)
      .then(d => { if (d) { setMaintenance(d.maintenanceMode || false); setAnnouncement(d.announcement || ""); } })
      .catch(() => {});
    apiFetch("/api/plan-info", {}, token)
      .then(d => { if (d?.plan) { setUserPlan(d.plan); setPlanInfo(d); } })
      .catch(() => {});
    apiFetch("/api/credits", {}, token)
      .then(d => { if (!d?.error) setUserCredits(d); })
      .catch(() => {});
    apiFetch("/api/bot-settings", {}, token)
      .then(d => {
        const s = d?.settings;
        setHasEcomStore(!!(s?.shopify?.enabled || s?.woocommerce?.enabled));
      })
      .catch(() => {});
    apiFetch("/api/features", {}, token)
      .then(d => { if (d?.features) setFeatures(d.features); })
      .catch(() => {});
    apiFetch("/api/overview-stats", {}, token)
      .then(d => { if (d?.mode) setOverviewStats(d); })
      .catch(() => {});
  }, [token]);



  // ── Show upgrade modal when contact limit is hit ────────────────
  useEffect(() => {
    if (!token || !planInfo) return;
    // Only nudge free / trial users — paid plans should never see the upgrade popup.
    if (!isFreeLikePlan) return;
    const limit = planInfo?.limits?.maxContacts;
    if (!limit || limit === -1) return;
    // Only trigger once per session when count first reaches/exceeds limit
    if (chats.length >= limit && !limitModalShown.current) {
      limitModalShown.current = true;
      showUpgradeModal("contacts");
    }
    // Reset so it can fire again if they somehow drop below (e.g. admin adjusts)
    if (chats.length < limit) limitModalShown.current = false;
  }, [chats.length, planInfo, token]);

  // ── Handle status changes ───────────────────────────────────────
  useEffect(() => {
    if (status === "logged_out")  showToast("WhatsApp session ended — please reconnect", "warn");
    if (status === "connected")   showToast("WhatsApp connected ✓", "success");
  }, [status]);

  const connect = async () => {
    setStatus("connecting");
    try {
      if (isOwner) {
        await apiFetch("/api/connect", { method: "POST" }, token);
        return;
      }

      let d = await apiFetch("/api/whatsapp/numbers", {}, token);
      let numberId = d?.numbers?.[0]?.id;
      if (!numberId) {
        const created = await apiFetch("/api/whatsapp/numbers", {
          method: "POST",
          body: JSON.stringify({ label: "My WhatsApp" }),
        }, token);
        numberId = created?.number?.id;
      }
      if (!numberId) throw new Error("No WhatsApp number available");
      await apiFetch(`/api/whatsapp/numbers/${numberId}/connect`, { method: "POST" }, token);
    }
    catch { setStatus("disconnected"); showToast("Connection failed — try again", "error"); }
  };

  const disconnect = async () => {
    try {
      if (isOwner) {
        await apiFetch("/api/disconnect", { method: "POST" }, token);
      } else {
        const d = await apiFetch("/api/whatsapp/numbers", {}, token);
        const numberId = d?.numbers?.[0]?.id;
        if (numberId) await apiFetch(`/api/whatsapp/numbers/${numberId}/disconnect`, { method: "POST" }, token);
      }
      setStatus("disconnected");
      showToast("WhatsApp disconnected");
    } catch { showToast("Disconnect failed", "error"); }
  };

  const login  = (t) => { localStorage.setItem("token", t); setToken(t); };
  const logout = () => { localStorage.removeItem("token"); setToken(null); setChats([]); setBroadcasts([]); };

  if (!token) return <AuthScreen onLogin={login} />;

  const unread = chats.reduce((s, c) => s + (c.unreadCount || 0), 0);
  const meta   = PAGE_META[page] || { title: page, sub: "" };

  return (
    <div style={{ display: "flex", height: "100vh", fontFamily: base.font, background: T.bg0, color: T.text1, overflow: "hidden", fontSize: 14 }}>

      {isMobile ? (
        <Sidebar
          page={page}
          setPage={p => { localStorage.setItem("zpliq_page", p); setPage(p); }}
          status={status} unread={unread} onLogout={logout} plan={userPlan}
          hasEcomStore={hasEcomStore} features={features} userRole={userRole} memberAccess={memberAccess}
          mobileOpen={sidebarOpen} onMobileClose={() => setSidebarOpen(false)}
        />
      ) : (
        <Sidebar
          page={page}
          setPage={p => { localStorage.setItem("zpliq_page", p); setPage(p); }}
          status={status} unread={unread} onLogout={logout} plan={userPlan}
          hasEcomStore={hasEcomStore} features={features} userRole={userRole} memberAccess={memberAccess}
        />
      )}

      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>

        {/* Topbar */}
        <div style={{
          height: 56, background: T.bg1,
          borderBottom: `1px solid ${T.border1}`,
          display: "flex", alignItems: "center",
          padding: isMobile ? "0 14px" : "0 28px", gap: isMobile ? 10 : 16, flexShrink: 0,
        }}>
          {/* Hamburger on mobile */}
          {isMobile && (
            <button
              onClick={() => setSidebarOpen(true)}
              style={{
                background: "none", border: "none", cursor: "pointer", padding: 6,
                display: "flex", flexDirection: "column", gap: 4, flexShrink: 0,
                borderRadius: 7,
              }}
            >
              <span style={{ display: "block", width: 18, height: 2, borderRadius: 2, background: T.text2 }} />
              <span style={{ display: "block", width: 18, height: 2, borderRadius: 2, background: T.text2 }} />
              <span style={{ display: "block", width: 18, height: 2, borderRadius: 2, background: T.text2 }} />
            </button>
          )}

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: isMobile ? 14 : 15, fontWeight: 700, color: T.text1, letterSpacing: "-0.015em", lineHeight: 1.3 }}>{meta.title}</div>
            {meta.sub && !isMobile && <div style={{ fontSize: 11, color: T.text3, marginTop: 1 }}>{meta.sub}</div>}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 6 : 10 }}>
            {/* Notification bell */}
            <NotificationBell token={token} socket={socket} />

            {/* Status pill */}
            <div style={{
              display: "flex", alignItems: "center", gap: 6,
              padding: isMobile ? "5px 8px" : "5px 12px", borderRadius: 20, fontSize: 11, fontWeight: 600,
              background: status === "connected"
                ? T.onlineDim
                : (status === "connecting" || status === "qr")
                ? T.amberDim : T.roseDim,
              color: status === "connected"
                ? T.online
                : (status === "connecting" || status === "qr")
                ? T.amber : T.rose,
              border: `1px solid ${status === "connected"
                ? `${T.online}44`
                : (status === "connecting" || status === "qr")
                ? `${T.amber}44` : `${T.rose}44`}`,
            }}>
              {status === "connecting" || status === "qr"
                ? <div style={{ width: 8, height: 8, border: `1.5px solid currentColor`, borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.7s linear infinite", opacity: 0.7 }} />
                : <span style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor",
                    animation: status === "connected" ? "pulseGlow 2.5s infinite" : "none" }} />
              }
              {!isMobile && (status === "connected" ? "Connected"
                : status === "qr" ? "Scan QR"
                : status === "connecting" ? "Connecting"
                : "Offline")}
            </div>

            {!isOwner && canAccessPage("numbers") ? (
              <Btn variant="outline" size="sm" onClick={() => { localStorage.setItem("zpliq_page", "numbers"); setPage("numbers"); }} style={{ fontSize: isMobile ? 10 : undefined, padding: isMobile ? "4px 8px" : undefined }}>
                <Icon name="wifi" size={12} color={T.text2} />
                {!isMobile && " My WhatsApp"}
              </Btn>
            ) : features?.whatsapp === false ? (
              <Btn variant="outline" size="sm" onClick={() => showUpgradeModal("whatsapp")} style={{ borderColor: "#f59e0b", color: "#f59e0b", fontSize: isMobile ? 10 : undefined, padding: isMobile ? "4px 8px" : undefined }}>
                {isMobile ? "Upgrade" : "WhatsApp Locked — Upgrade"}
              </Btn>
            ) : (
              <>
                {(status === "disconnected" || status === "logged_out") && (
                  <Btn variant="primary" size="sm" onClick={() => setShowConnModal(true)}>
                    <Icon name="wifi" size={12} color="#fff" />
                    {!isMobile && " Connect WhatsApp"}
                  </Btn>
                )}
                {status === "connected" && !isMobile && (
                  <Btn variant="outline" size="sm" onClick={disconnect}>Disconnect</Btn>
                )}
              </>
            )}
          </div>
        </div>

        {/* Page content */}
        <div style={{ flex: 1, display: "flex", overflow: "hidden", minWidth: 0 }}>
          {(() => {
            const f = features || {};

            // Locked screen — shows upgrade CTA inline + opens modal
            const locked = (label, reason) => (
              <div style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:16, color: T.text3 }}>
                <div style={{ width:56, height:56, borderRadius:16, background:"rgba(99,102,241,0.1)", display:"flex", alignItems:"center", justifyContent:"center" }}>
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="1.6"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                </div>
                <div style={{ textAlign:"center" }}>
                  <div style={{ fontSize:16, fontWeight:700, color:T.text1, marginBottom:6 }}>{label} is not available on your plan</div>
                  <div style={{ fontSize:13, color:T.text3 }}>Upgrade your plan to unlock this feature.</div>
                </div>
                <button
                  onClick={() => showUpgradeModal(reason)}
                  style={{
                    padding:"10px 24px", borderRadius:10, border:"none", cursor:"pointer",
                    background:"linear-gradient(135deg,#6366f1,#8b5cf6)", color:"#fff",
                    fontSize:13, fontWeight:700, fontFamily:base.font,
                    display:"flex", alignItems:"center", gap:8,
                  }}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
                  View Plans & Upgrade
                </button>
              </div>
            );

            return (<>
              {page === "overview"      && (canAccessPage("overview") ? <OverviewPage  token={token} status={status} qr={qr} onDisconnect={disconnect} chats={chats} broadcasts={broadcasts} onShowModal={isOwner ? () => setShowConnModal(true) : connect} sessionInfo={sessionInfo} maintenance={maintenance} announcement={announcement} planInfo={planInfo} credits={userCredits} setPage={setPage} overviewStats={overviewStats} hasEcomStore={hasEcomStore} onLimitHit={showUpgradeModal} showToast={showToast} /> : locked("Overview", "overview"))}
              {page === "chats"         && (canAccessPage("chats") ? <ChatsPage     token={token} chats={chats} setChats={setChats} socket={socket} showToast={showToast} onLimitHit={showUpgradeModal} planInfo={planInfo} /> : locked("Chats", "chats"))}
              {page === "broadcast"     && (canAccessPage("broadcast") && f.broadcast    !== false ? <BroadcastPage token={token} chats={chats} broadcasts={broadcasts} setBroadcasts={setBroadcasts} socket={socket} showToast={showToast} plan={userPlan} setPage={setPage} onLimitHit={showUpgradeModal} /> : locked("Broadcast", "broadcast"))}
              {page === "schedule"      && (canAccessPage("schedule") && f.schedule     !== false ? <SchedulePage  token={token} showToast={showToast} />                                                                                                                                                         : locked("Schedule", "schedule"))}
              {page === "knowledge"     && (canAccessPage("knowledge") && f.knowledge    !== false ? <KnowledgePage token={token} showToast={showToast} isOwner={isOwner} />                                                                                                                                      : locked("Knowledge Base", "knowledge"))}
              {page === "safety"        && (canAccessPage("safety") && f.safety       !== false ? <SafetyPage    token={token} showToast={showToast} />                                                                                                                                                         : locked("Safety & Anti-Ban", "safety"))}
              {page === "insights"      && (canAccessPage("insights") && f.aiInsights   !== false ? <InsightsPage  token={token} showToast={showToast} setPage={setPage} />                                                                                                                                       : locked("AI Insights", "aiInsights"))}
              {page === "plan"          && (isOwner ? <PlanPage token={token} setPage={setPage} /> : locked("My Plan", "plan"))}
              {page === "contacts"      && (canAccessPage("contacts") && f.contacts     !== false ? <ContactsPage  token={token} showToast={showToast} onLimitHit={showUpgradeModal} planInfo={planInfo} />                                                                                                        : locked("Contacts", "contacts"))}
              {page === "pipeline"      && (canAccessPage("pipeline") && f.pipeline     === true ? <PipelinePage     token={token} showToast={showToast} socket={socket} userRole={userRole} />                                                                                                                   : locked("Lead Gen Pipeline", "pipeline"))}
              {page === "ecom_pipeline" && (canAccessPage("ecom_pipeline") && f.ecomPipeline === true ? <EcomPipelinePage token={token} showToast={showToast} socket={socket} />                                                                                                                                      : locked("E-com Pipeline", "ecomPipeline"))}
              {page === "profile"       && <ProfilePage   token={token} showToast={showToast} status={status} onLogout={logout} />}
              {page === "shopify"       && (canAccessPage("shopify") && f.shopify      === true ? <ShopifyPage      token={token} plan={userPlan} setPage={setPage} />                                                                                                                                          : locked("Shopify Integration", "shopify"))}
              {page === "woocommerce"   && (canAccessPage("woocommerce") && f.woocommerce  === true ? <WooCommercePage  token={token} plan={userPlan} setPage={setPage} />                                                                                                                                          : locked("WooCommerce Integration", "woocommerce"))}
              {page === "messenger"     && (canAccessPage("messenger") && f.messenger    === true ? <MessengerPage    token={token} />                                                                                                                                                                            : locked("Facebook Messenger", "messenger"))}
              {page === "instagram"     && (canAccessPage("instagram") && f.instagram    === true ? <InstagramPage    token={token} />                                                                                                                                                                            : locked("Instagram DMs", "instagram"))}
              {page === "outbound"      && (canAccessPage("outbound") && isOwner && f.outbound !== false ? <OutboundPage token={token} socket={socket} showToast={showToast} /> : locked("Outbound Leads", "outbound"))}
              {page === "todo"          && (f.todo !== false ? <TodoPage token={token} showToast={showToast} /> : locked("To Do", "todo"))}
              {page === "email"         && (isOwner && f.email !== false ? <EmailPage token={token} showToast={showToast} /> : locked("Email Assistant", "email"))}
              {page === "team"          && (canAccessPage("team") && isOwner ? <TeamPage token={token} showToast={showToast} /> : locked("Team Members", "team"))}
            </>);
          })()}
        </div>
      </div>

      {showConnModal && (
        <ConnectionModal onClose={() => setShowConnModal(false)} onQR={connect} onMeta={() => { setStatus("connected"); setShowConnModal(false); }} />
      )}

      <UpgradeModal
        open={upgradeModal.open}
        reason={upgradeModal.reason}
        planInfo={planInfo}
        onClose={() => setUpgradeModal({ open: false, reason: null })}
      />

      {apiBusyCount > 0 && (
        <div style={{ position:"fixed",top:isMobile?10:14,right:isMobile?12:24,zIndex:9998,padding:"8px 12px",borderRadius:999,background:T.bg2,color:T.text2,border:`1px solid ${T.border1}`,boxShadow:T.shadowLg,display:"flex",alignItems:"center",gap:8,fontSize:12,fontWeight:700,pointerEvents:"none",animation:"fadeIn 0.15s ease" }}>
          <div style={{ width:13,height:13,border:`2px solid ${T.border2}`,borderTopColor:T.indigo,borderRadius:"50%",animation:"spin 0.7s linear infinite" }} />
          Working...
        </div>
      )}

      {/* Toast stack */}
      <div style={{ position: "fixed", bottom: isMobile ? 16 : 24, right: isMobile ? 12 : 24, left: isMobile ? 12 : "auto", zIndex: 9999, display: "flex", flexDirection: "column", gap: 8, pointerEvents: "none" }}>
        {toasts.map(t => {
          const isErr  = t.type === "error";
          const isWarn = t.type === "warn";
          const col = isErr ? T.rose : isWarn ? T.amber : T.online;
          const dim = isErr ? T.roseDim : isWarn ? T.amberDim : T.onlineDim;
          return (
            <div key={t.id} style={{
              padding: "11px 16px", borderRadius: 11, fontSize: 13, fontWeight: 500,
              background: dim, color: col, border: `1px solid ${col}33`,
              boxShadow: T.shadowLg, animation: "toastIn 0.3s ease",
              display: "flex", alignItems: "center", gap: 9,
              maxWidth: isMobile ? "100%" : 360, pointerEvents: "auto",
            }}>
              {isErr
                ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" style={{flexShrink:0}}><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
                : isWarn
                ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" style={{flexShrink:0}}><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{flexShrink:0}}><polyline points="20 6 9 17 4 12"/></svg>
              }
              {t.msg}
            </div>
          );
        })}
      </div>

      {/* Reminder notification cards */}
      <div style={{ position: "fixed", bottom: isMobile ? 16 : 28, right: isMobile ? 12 : 28, zIndex: 10000, display: "flex", flexDirection: "column", gap: 12, pointerEvents: "none", alignItems: "flex-end" }}>
        {reminderCards.map(card => (
          <ReminderCard
            key={card.id}
            card={card}
            onDismiss={() => setReminderCards(p => p.filter(c => c.id !== card.id))}
          />
        ))}
      </div>
    </div>
  );
}

export default function App() {
  const inviteToken = new URLSearchParams(window.location.search).get("token");
  const isInvitePage = inviteToken && (
    window.location.pathname === "/invite" || window.location.search.includes("token=")
  );

  if (isInvitePage) {
    return (
      <ThemeProvider>
        <InvitePage onLogin={(t) => {
          localStorage.setItem("token", t);
          window.history.replaceState({}, "", "/");
          window.location.reload();
        }} />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <AppInner />
    </ThemeProvider>
  );
}
